/**
 * The notation engine, loaded once.
 *
 * Verovio is seven megabytes of WebAssembly, so it is imported dynamically: the rest of the
 * app is not made slower by a board most visits never open. The module-level promise is the
 * cache -- a second call while the first is still loading waits for the same one, and a
 * module already built is returned at once.
 *
 * A toolkit is not the same thing as the module. One module can make as many toolkits as
 * are wanted at no further download, and each holds one document and one set of options.
 * That is why printing gets its own: the editor's toolkit is holding the score as it is
 * drawn on the desk, and laying the same score out on paper would leave it holding pages
 * that no longer match what is on screen, so playback would highlight the wrong one.
 */

import type { NotationEngine } from "@app/score-editor";

/** A toolkit, which is an engine that can also be given back. */
type Toolkit = NotationEngine & { destroy(): void };

let loading: Promise<() => Toolkit> | null = null;

async function load(): Promise<() => Toolkit> {
  const [wasm, esm] = await Promise.all([import("verovio/wasm"), import("verovio/esm")]);
  const module = await wasm.default();
  return () => new esm.VerovioToolkit(module);
}

/** A way to make toolkits, loading the module on the first call. */
function toolkits(): Promise<() => Toolkit> {
  loading ??= load();
  return loading;
}

let engine: Promise<NotationEngine> | null = null;

/** The engine the editor draws with, made once and kept. */
export function notationEngine(): Promise<NotationEngine> {
  engine ??= toolkits().then((make) => make());
  return engine;
}

/**
 * A second engine, for the length of one job.
 *
 * It is destroyed afterwards however the job ends, so printing a score does not leave a
 * whole engraved document in memory for the rest of the session.
 */
export async function withSpareEngine<T>(use: (spare: NotationEngine) => T): Promise<T> {
  const spare = (await toolkits())();
  try {
    return use(spare);
  } finally {
    spare.destroy();
  }
}
